import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  TextField,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Stack,
  Fab,
  Badge,
  TablePagination,
  TableSortLabel,
  Select,
  FormControl,
  InputLabel,
  Skeleton,
  Slide,
  Avatar,
  alpha,
  Tabs,
  Tab,
} from '@mui/material';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import DashboardIcon from '@mui/icons-material/Dashboard';
import {
  Add,
  Search,
  Person,
  LocationOn,
  Business,
  CheckCircle,
  Block,
  Cancel,
  PeopleAlt,
  Refresh,
  Warning,
  WhatsApp as WhatsAppIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';

import { Client, Contract } from '../types';
import { clientService } from '../services/clientService';
import ClientForm from '../components/forms/ClientForm';
import { formatarCPFCNPJ, formatarData } from '../utils/formatToView';
import ClientesListarTab from './clients/ClientesListarTab';
import ClientesDashboardTab from './clients/ClientesDashboardTab';
import { getStatusColor, getStatusLabel } from '../utils/contractStatusUtils';
import { useAuth } from '../contexts/AuthContext';


interface KPICard {
  titulo: string;
  valor: string | number;
  qtde_contratos: string | number;
  icone: React.ReactElement;
  cor: string;
  gradiente: string;
  subtitulo?: string;
}

const ClientsPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const primario = theme.palette.primary.main;
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useAuth();

  const canCreateClient = user?.perfil === 'administrador' || user?.permissions?.includes('clientes.criar');

  // Obter parâmetros do escritório da URL
  const escritorioId = searchParams.get('escritorio_id');
  const escritorioNome = searchParams.get('escritorio_nome');

  // Estados simplificados - removidos estados redundantes
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeCategory, setActiveCategory] = useState<'all' | 'active' | 'blocked' | 'inactive' | 'no-contract'>('all');

  const [clientesSelectedCount, setClientesSelectedCount] = useState(0);
  const [clientesNotifyTrigger, setClientesNotifyTrigger] = useState(0);

  // Estado da aba principal (0 = Clientes, 1 = Dashboard)
  const [mainTab, setMainTab] = useState(0);

  // Query principal para buscar todos os clientes - buscar mais registros para filtro local funcionar bem
  const {
    data: clientsResponse,
    isLoading: isLoadingClients,
    error: clientsError,
    refetch: refetchClients
  } = useQuery({
    queryKey: ['clients', escritorioId], // Incluir escritorioId na key para cache separado
    queryFn: () => clientService.getAll({
      page: 1,
      limit: 2000, // Buscar mais clientes para filtro local
      status: undefined, // Sempre buscar todos os clientes
      ...(escritorioId && { escritorio_id: escritorioId }) // Adicionar filtro se escritório estiver presente
    }),
    staleTime: 0, // Sem cache - sempre busca dados frescos
    refetchOnMount: true, // Sempre refaz a busca ao montar o componente
    refetchOnWindowFocus: true, // Refaz a busca ao focar na janela
    refetchInterval: 10000, // Refetch a cada 10 segundos
    refetchIntervalInBackground: true,
    refetchOnReconnect: true, // Refaz a busca ao reconectar
  });

  // Estados para paginação e ordenação
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(30);
  const [orderBy, setOrderBy] = useState<keyof Client>('id');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Extrair estatísticas da resposta principal
  const stats = clientsResponse?.stats;
  const statsLoading = isLoadingClients;

  // Função para forçar refresh de todas as queries
  const refreshAllData = async () => {
    try {
      setError(null); // Limpar erros anteriores
      await refetchClients();
    } catch (error) {
      console.error('Erro ao atualizar dados:', error);
      setError('Erro ao atualizar dados. Tente novamente.');
    }
  };

  // Verificar se alguma query está sendo atualizada
  const isAnyQueryRefetching = isLoadingClients;

  // Função para atualizar estados de loading das categorias baseado nas queries
  const getCategoryLoading = (category: string) => {
    return isLoadingClients; // Todas as categorias usam a mesma query
  };

  // Reset da página quando o termo de busca mudar
  useEffect(() => {
    setPage(0);
  }, [searchTerm]);

  // Forçar refetch quando a janela ganhar foco (solução alternativa)
  useEffect(() => {
    const handleFocus = () => {
      refreshAllData();
    };

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refreshAllData();
      }
    };

    window.addEventListener('focus', handleFocus);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      window.removeEventListener('focus', handleFocus);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Função para verificar se um cliente tem contratos bloqueados
  const hasBlockedContracts = (client: Client): boolean => {
    const contracts = client.contratos || [];
    return contracts.some(contract => contract.status === 'bloqueado');
  };

  // Função para obter o status real do cliente (considerando contratos bloqueados)
  const getClientRealStatus = (client: Client): string => {
    if (hasBlockedContracts(client)) {
      return 'bloqueado';
    }
    return client.status;
  };

  // Filtrar clientes por categoria e termo de busca
  const getFilteredClients = (category: 'all' | 'active' | 'inactive' | 'blocked' | 'no-contract') => {
    const allClients = clientsResponse?.data || [];

    const filtered = allClients.filter(client => {
      const searchLower = searchTerm.toLowerCase();
      const matchesSearch =
        client.nome.toLowerCase().includes(searchLower) ||
        client.cpf_cnpj.includes(searchTerm) ||
        (client.cidade && client.cidade.toLowerCase().includes(searchLower)) ||
        (client.estado && client.estado.toLowerCase().includes(searchLower)) ||
        (client.endereco && client.endereco.toLowerCase().includes(searchLower)) ||
        (client.bairro && client.bairro.toLowerCase().includes(searchLower));

      const realStatus = getClientRealStatus(client);

      switch (category) {
        case 'all':
          // Categoria "Todos" - exibe todos os clientes ativos independentemente de contratos bloqueados
          return client.status === 'ativo' && matchesSearch;
        case 'active':
          // Categoria "Ativos" - apenas clientes sem contratos bloqueados
          return realStatus === 'ativo' && matchesSearch;
        case 'inactive':
          // Categoria "Inativos" - apenas clientes com status inativo
          return client.status === 'inativo' && matchesSearch;
        case 'blocked':
          // Categoria "Bloqueados" - apenas clientes com contratos bloqueados
          return realStatus === 'bloqueado' && matchesSearch;
        case 'no-contract':
          // Categoria "Sem Contrato" - clientes ativos sem nenhum contrato
          return client.status === 'ativo' && (!client.contratos || client.contratos.length === 0) && matchesSearch;
        default:
          return false;
      }
    });

    return filtered;
  };

  // Função para ordenar clientes
  const sortClients = (clients: Client[]) => {
    return clients.sort((a, b) => {
      let aValue: any = a[orderBy];
      let bValue: any = b[orderBy];

      // Tratar valores undefined
      if (aValue === undefined) aValue = '';
      if (bValue === undefined) bValue = '';

      // Tratar campos de data
      if (orderBy === 'createdAt' || orderBy === 'updatedAt') {
        const dateA = new Date(aValue || 0);
        const dateB = new Date(bValue || 0);

        if (dateA < dateB) {
          return order === 'asc' ? -1 : 1;
        }
        if (dateA > dateB) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      }

      // Tratar campos numéricos (como id)
      if (orderBy === 'id' || typeof aValue === 'number' || typeof bValue === 'number') {
        const numA = Number(aValue) || 0;
        const numB = Number(bValue) || 0;

        if (numA < numB) {
          return order === 'asc' ? -1 : 1;
        }
        if (numA > numB) {
          return order === 'asc' ? 1 : -1;
        }
        return 0;
      }

      // Converter para string para comparação de outros campos
      if (typeof aValue === 'string') aValue = aValue.toLowerCase();
      if (typeof bValue === 'string') bValue = bValue.toLowerCase();

      if (aValue < bValue) {
        return order === 'asc' ? -1 : 1;
      }
      if (aValue > bValue) {
        return order === 'asc' ? 1 : -1;
      }
      return 0;
    });
  };

  // Função para paginar clientes
  const paginateClients = (clients: Client[]) => {
    const startIndex = page * rowsPerPage;
    const endIndex = startIndex + rowsPerPage;
    return clients.slice(startIndex, endIndex);
  };

  // Obter clientes filtrados, ordenados e paginados
  const getProcessedClients = (category: 'all' | 'active' | 'inactive' | 'blocked' | 'no-contract') => {
    const filtered = getFilteredClients(category);
    const sorted = sortClients(filtered);
    return paginateClients(sorted);
  };

  // Obter todos os clientes filtrados (para contagem total)
  const allClients = getFilteredClients('all');
  const activeClientsFiltered = getFilteredClients('active');
  const inactiveClientsFiltered = getFilteredClients('inactive');
  const blockedClients = getFilteredClients('blocked');
  const noContractClientsFiltered = getFilteredClients('no-contract');

  // Obter clientes processados para exibição
  const allClientsDisplay = getProcessedClients('all');
  const activeClientsDisplay = getProcessedClients('active');
  const inactiveClientsDisplay = getProcessedClients('inactive');
  const blockedClientsDisplay = getProcessedClients('blocked');
  const noContractClientsDisplay = getProcessedClients('no-contract');

  // Abrir formulário para novo cliente
  const handleNewClient = () => {
    setEditingClient(null);
    setFormOpen(true);
  };

  // Abrir formulário para editar cliente
  const handleEditClient = (client: Client) => {
    setEditingClient(client);
    setFormOpen(true);
  };

  // Fechar formulário
  const handleCloseForm = () => {
    setFormOpen(false);
    setEditingClient(null);
  };

  // Sucesso ao salvar cliente
  const handleClientSuccess = (client: Client) => {
    // Invalidar queries para forçar recarregamento
    queryClient.invalidateQueries({ queryKey: ['clients'] });

    // Forçar refresh imediato
    refreshAllData();

    handleCloseForm();

    // Direcionar para a página do cliente se foi um cadastro novo
    if (!editingClient) {
      navigate(`/clients/${client.id}`);
    }
  };

  // Excluir cliente
  const handleDeleteClient = async (client: Client) => {
    if (window.confirm(`Tem certeza que deseja excluir o cliente ${client.nome}?`)) {
      try {
        await clientService.delete(client.id);

        // Invalidar queries para forçar recarregamento
        queryClient.invalidateQueries({ queryKey: ['clients'] });

        // Forçar refresh imediato
        refreshAllData();
      } catch (err: any) {
        setError(err.response?.data?.message || 'Erro ao excluir cliente');
      }
    }
  };

  // Visualizar detalhes do cliente
  const handleViewClient = (client: Client) => {
    navigate(`/clients/${client.id}`);
  };

  // Handlers para paginação e ordenação

  // Handlers para paginação e ordenação
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: keyof Client) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    setPage(0);
  };

  const kpiCards: (KPICard & { category: 'all' | 'active' | 'blocked' | 'inactive' | 'no-contract' })[] = [
    {
      titulo: 'Total de Clientes',
      valor: statsLoading ? '...' : (stats?.total || 0),
      qtde_contratos: statsLoading ? '...' : (stats?.totalContratos || 0),
      icone: <PeopleAlt />,
      cor: theme.palette.primary.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
      subtitulo: 'Quantidade de clientes cadastrados',
      category: 'all',
    },
    {
      titulo: 'Clientes Ativos',
      valor: statsLoading ? '...' : (stats?.ativos || 0),
      qtde_contratos: statsLoading ? '...' : (stats?.contratosAtivos || 0),
      icone: <PeopleAlt />,
      cor: theme.palette.success.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.success.main} 0%, ${theme.palette.success.dark} 100%)`,
      subtitulo: 'Quantidade de clientes ativos',
      category: 'active',
    },
    {
      titulo: 'Clientes Bloqueados',
      valor: statsLoading ? '...' : (stats?.bloqueados || 0),
      qtde_contratos: statsLoading ? '...' : (stats?.contratosBloqueados || ''),
      icone: <PeopleAlt />,
      cor: theme.palette.warning.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.warning.main} 0%, ${theme.palette.warning.dark} 100%)`,
      subtitulo: 'Quantidade de clientes bloqueados',
      category: 'blocked',
    },
    {
      titulo: 'Clientes Sem Contrato',
      valor: statsLoading ? '...' : (stats?.semContrato || 0),
      qtde_contratos: statsLoading ? '...' : '',
      icone: <PeopleAlt />,
      cor: theme.palette.info.main,
      gradiente: `linear-gradient(135deg, ${theme.palette.info.main} 0%, ${theme.palette.info.dark} 100%)`,
      subtitulo: 'Clientes ativos sem nenhum contrato',
      category: 'no-contract',
    },
    {
      titulo: 'Clientes Desativados',
      valor: statsLoading ? '...' : (stats?.inativos || 0),
      qtde_contratos: statsLoading ? '...' : '',
      icone: <PeopleAlt />,
      cor: theme.palette.grey[500],
      gradiente: `linear-gradient(135deg, ${theme.palette.grey[500]} 0%, ${theme.palette.grey[700]} 100%)`,
      subtitulo: 'Quantidade de clientes desativados',
      category: 'inactive',
    },
  ];

  // Componente KPI Card moderno
  const ModernKPICard: React.FC<KPICard & { loading?: boolean; delay?: number; onClick?: () => void; isActive?: boolean }> = ({
    titulo,
    valor,
    qtde_contratos,
    icone,
    cor,
    gradiente,
    subtitulo,
    loading,
    delay = 0,
    onClick,
    isActive = false,
  }) => {
    const theme = useTheme();

    if (loading) {
      return (
        <Card elevation={2} sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Skeleton variant="text" width="60%" />
            <Skeleton variant="text" width="40%" height={50} />
            <Skeleton variant="text" width="30%" />
          </CardContent>
        </Card>
      );
    }

    return (
      <Slide direction="up" in timeout={600 + delay}>
        <Card
          elevation={isActive ? 8 : 2}
          onClick={onClick}
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: '100%',
            borderRadius: 3,
            cursor: onClick ? 'pointer' : 'default',
            transition: 'all 0.3s ease-in-out',
            border: isActive ? `2px solid ${cor}` : 'none',
            transform: isActive ? 'scale(1.02)' : 'none',
            '&:hover': {
              transform: onClick ? 'translateY(-4px) scale(1.02)' : 'translateY(-4px)',
              boxShadow: theme.shadows[12],
            },
          }}
        >
          <Box
            sx={{
              background: gradiente,
              height: 4,
              width: '100%',
            }}
          />
          <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.25 }}>
              <Box>
                <Typography color="text.secondary" variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                  {titulo}
                </Typography>
                {subtitulo && (
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {subtitulo}
                  </Typography>
                )}
              </Box>
              <Avatar
                sx={{
                  bgcolor: alpha(cor, 0.1),
                  color: cor,
                  width: 32,
                  height: 32,
                }}
              >
                {icone}
              </Avatar>
            </Box>
            <Typography variant="h6" fontWeight="bold" color="text.primary" sx={{ mb: 0.25, fontSize: '1.1rem' }}>
              {typeof valor === 'number' ? valor.toLocaleString('pt-BR') : valor}
            </Typography>
            {qtde_contratos && (
              <Typography variant="body2" color="text.primary" sx={{ mb: 0.25, fontSize: '0.75rem' }}>
                Qtde de contratos: {typeof qtde_contratos === 'number' ? qtde_contratos.toLocaleString('pt-BR') : qtde_contratos}
              </Typography>
            )}
            {!qtde_contratos && (
              <Box sx={{ height: '12px' }} />
            )}
            <Box sx={{ width: '100%', height: '1px', bgcolor: 'divider', my: 0.25 }} />
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant="caption"
                color={onClick ? 'primary.main' : 'success.main'}
                fontWeight={600}
                sx={{ fontSize: '0.7rem' }}
              >
                {onClick ? 'clique para exibir' : 'clique para ver mais'}
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Slide>
    );
  };

  // Componente para renderizar cards de clientes (mobile)
  const ClientCard = ({ client }: { client: Client }) => {
    const realStatus = getClientRealStatus(client);
    const isBlocked = realStatus === 'bloqueado';
    const blockedContractsCount = client.contratos?.filter(c => c.status === 'bloqueado').length || 0;

    return (
      <Box
        sx={{
          cursor: 'pointer',
          transition: 'all 0.2s ease-in-out',
          '&:hover': {
            elevation: 4,
            transform: 'translateY(-2px)',
            boxShadow: (theme) => theme.shadows[4],
          },
          '&:active': {
            transform: 'translateY(0px)',
            boxShadow: (theme) => theme.shadows[2],
          }
        }}
      >
        <Box onClick={() => handleViewClient(client)} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" component="div" fontWeight="bold" sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                {client.tipo_pessoa === 'pf' ? <Person sx={{ mr: 1, fontSize: 20 }} /> : <Business sx={{ mr: 1, fontSize: 20 }} />}
                {client.nome}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {client.tipo_pessoa === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Chip
                label={getStatusLabel(realStatus)}
                color={getStatusColor(realStatus) as any}
                size="small"
              />
            </Box>
          </Box>

          <Stack spacing={1}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                ID:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                #{client.id}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Documento:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {formatarCPFCNPJ(client.cpf_cnpj)}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Localização:
              </Typography>
              <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center' }}>
                <LocationOn sx={{ mr: 0.5, fontSize: 16 }} />
                {client.cidade}, {client.estado}
              </Typography>
            </Box>

            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="body2" color="text.secondary">
                Data Cadastro:
              </Typography>
              <Typography variant="body2" fontWeight="medium">
                {client.createdAt ? new Date(client.createdAt).toLocaleDateString('pt-BR') : '-'}
              </Typography>
            </Box>

            {isBlocked && (
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2" color="text.secondary">
                  Contratos Bloqueados:
                </Typography>
                <Typography variant="body2" color="warning.main" fontWeight="medium">
                  {blockedContractsCount}
                </Typography>
              </Box>
            )}
          </Stack>

          <Box sx={{
            mt: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            opacity: 0.6
          }}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              Toque para visualizar
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  };

  // Componente para renderizar tabela de clientes (desktop/tablet)
  const ClientTable = ({ clients, totalCount }: { clients: Client[], totalCount: number }) => (
    <Paper>
      <TableContainer
        sx={{
          maxHeight: isTablet ? 600 : 'none',
          overflowX: 'auto',
          '& .MuiTable-root': {
            minWidth: isTablet ? 800 : 650,
          },
        }}
      >
        <Table stickyHeader={isTablet}>
          <TableHead>
            <TableRow sx={{
              background: `linear-gradient(15deg, ${theme.palette.primary.main} 50%, ${theme.palette.primary.dark} 50%)`,
              '& .MuiTableCell-head': {
                color: 'white',
                fontWeight: 600,
                fontSize: '0.8rem',
                borderBottom: '2px solid rgba(255,255,255,0.2)',
                padding: '2px 12px',
                '&:hover': {
                  backgroundColor: 'rgba(255,255,255,0.1)',
                },
                '&.MuiTableSortLabel-root': {
                  color: 'white',
                  '&:hover': {
                    color: 'rgba(255,255,255,0.8)',
                  },
                  '&.Mui-active': {
                    color: 'white',
                    '& .MuiTableSortLabel-icon': {
                      color: 'white',
                    },
                  },
                },
              },
            }}>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'id'}
                  direction={orderBy === 'id' ? order : 'asc'}
                  onClick={() => handleRequestSort('id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'nome'}
                  direction={orderBy === 'nome' ? order : 'asc'}
                  onClick={() => handleRequestSort('nome')}
                >
                  Cliente
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'cpf_cnpj'}
                  direction={orderBy === 'cpf_cnpj' ? order : 'asc'}
                  onClick={() => handleRequestSort('cpf_cnpj')}
                >
                  Documento
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'cidade'}
                  direction={orderBy === 'cidade' ? order : 'asc'}
                  onClick={() => handleRequestSort('cidade')}
                >
                  Localização
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'status'}
                  direction={orderBy === 'status' ? order : 'asc'}
                  onClick={() => handleRequestSort('status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={orderBy === 'createdAt'}
                  direction={orderBy === 'createdAt' ? order : 'asc'}
                  onClick={() => handleRequestSort('createdAt')}
                >
                  Data Cadastro
                </TableSortLabel>
              </TableCell>
              {activeCategory === 'blocked' && <TableCell>Contratos Bloqueados</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {getCategoryLoading(activeCategory) ? (
              <TableRow>
                <TableCell colSpan={activeCategory === 'blocked' ? 7 : 6} align="center">
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : clients.length === 0 ? (
              <TableRow>
                <TableCell colSpan={activeCategory === 'blocked' ? 7 : 6} align="center">
                  <Typography variant="body2" color="textSecondary">
                    {searchTerm ? 'Nenhum cliente encontrado' : 'Nenhum cliente nesta categoria'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              clients.map((client) => {
                const realStatus = getClientRealStatus(client);
                const isBlocked = realStatus === 'bloqueado';
                const blockedContractsCount = client.contratos?.filter(c => c.status === 'bloqueado').length || 0;

                return (
                  <TableRow key={client.id} hover>
                    <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1, width: 50, maxWidth: 50 }} onClick={() => handleViewClient(client)}>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                        #{client.id}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1, width: 280, maxWidth: 280 }} onClick={() => handleViewClient(client)}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                        {client.tipo_pessoa === 'pf' ? <Person sx={{ color: 'primary.main', fontSize: 14 }} /> : <Business sx={{ color: 'primary.main', fontSize: 14 }} />}
                        <Box>
                          <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 1.1, mb: 0.1, fontSize: '0.8rem' }}>
                            {client.nome}
                          </Typography>
                          <Typography variant="caption" color="textSecondary" sx={{ lineHeight: 1, fontSize: '0.65rem' }}>
                            {client.tipo_pessoa === 'pf' ? 'Pessoa Física' : 'Pessoa Jurídica'}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1 }} onClick={() => handleViewClient(client)}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {formatarCPFCNPJ(client.cpf_cnpj)}
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1 }} onClick={() => handleViewClient(client)}>
                      <Box>
                        <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.2 }}>
                          <LocationOn sx={{ fontSize: 12 }} /> {client.endereco}, {client.numero}
                        </Typography>
                        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.7rem', lineHeight: 1 }}>
                          {client.bairro} - {client.cidade}, {client.estado}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ py: 0.5, px: 1 }}>
                      <Chip
                        label={getStatusLabel(realStatus)}
                        color={getStatusColor(realStatus) as any}
                        size="small"
                        sx={{ fontSize: '0.65rem', height: 18 }}
                      />
                    </TableCell>
                    <TableCell sx={{ cursor: 'pointer', py: 0.5, px: 1 }} onClick={() => handleViewClient(client)}>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {client.createdAt ? formatarData(client.createdAt) : '-'}
                      </Typography>
                    </TableCell>
                    {activeCategory === 'blocked' && (
                      <TableCell sx={{ py: 0.5, px: 1 }}>
                        <Typography variant="body2" color="warning.main" fontWeight="medium" sx={{ fontSize: '0.8rem' }}>
                          {blockedContractsCount}
                        </Typography>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        rowsPerPageOptions={[20, 30, 50, 100]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        labelRowsPerPage="Linhas por pág:"
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        sx={{
          '.MuiTablePagination-toolbar': {
            minHeight: 32,
            flexWrap: 'nowrap',
            justifyContent: 'center',
            gap: 1,
            p: 0,
            '& .MuiTablePagination-spacer': { display: 'none' },
          },
          '.MuiTablePagination-selectLabel': { m: 0, fontSize: '0.78rem' },
          '.MuiTablePagination-select': { py: 0.5, pr: 2.5, fontSize: '0.78rem' },
          '.MuiTablePagination-displayedRows': { m: 0, fontSize: '0.78rem' },
          '.MuiTablePagination-actions': { ml: 0.5, '& .MuiIconButton-root': { p: 0.5 } },
        }}
      />
    </Paper>
  );

  // Componente para renderizar conteúdo de uma categoria
  const CategoryContent = ({
    clients,
    category,
    title,
    emptyMessage,
    totalCount
  }: {
    clients: Client[],
    category: 'all' | 'active' | 'blocked' | 'inactive' | 'no-contract',
    title: string,
    emptyMessage: string,
    totalCount: number
  }) => (
    <Box sx={{ pl: 1.5, pr: 1.5 }}>
      {/* Conteúdo da categoria */}
      {isMobile ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {getCategoryLoading(category) ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : clients.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" color="textSecondary">
                {searchTerm ? 'Nenhum cliente encontrado' : emptyMessage}
              </Typography>
            </Box>
          ) : (
            clients.map((client) => <ClientCard key={client.id} client={client} />)
          )}
        </Box>
      ) : (
        <ClientTable clients={clients} totalCount={totalCount} />
      )}
    </Box>
  );

  return (
    <Box sx={{ p: 0.5 }}>
      <Paper
        elevation={2}
        sx={{
          mb: 1,
          borderRadius: 2,
          overflow: 'hidden',
          background: `linear-gradient(135deg, ${alpha(primario, 0.04)} 0%, ${alpha(theme.palette.secondary.main, 0.04)} 100%)`,
        }}
      >
      {/* Cabeçalho */}
      <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar sx={{ bgcolor: alpha(primario, 0.12), color: primario, width: 40, height: 40 }}>
              <Person sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} sx={{ fontSize: '1.05rem', lineHeight: 1.2 }}>
                Clientes
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.72rem' }}>
                Gerencie clientes do escritório
              </Typography>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {mainTab === 0 && clientesSelectedCount > 0 && (
              <Badge badgeContent={clientesSelectedCount} color="success">
                <Button
                  variant="contained"
                  startIcon={<WhatsAppIcon />}
                  color="success"
                  onClick={() => setClientesNotifyTrigger(t => t + 1)}
                  size="small"
                  sx={{ minWidth: { md: 160 }, whiteSpace: 'nowrap' }}
                >
                  Notificar Clientes
                </Button>
              </Badge>
            )}
            <Button
              variant="outlined"
              startIcon={isAnyQueryRefetching ? <CircularProgress size={16} /> : <Refresh />}
              onClick={refreshAllData}
              size="small"
              disabled={isAnyQueryRefetching}
              sx={{ minWidth: { md: 120 } }}
            >
              {isAnyQueryRefetching ? 'Atualizando...' : 'Atualizar'}
            </Button>
            {canCreateClient && (
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleNewClient}
                size="small"
                sx={{ minWidth: { md: 140 } }}
              >
                Novo Cliente
              </Button>
            )}
          </Box>
        </Box>
      

      {/* ── ABAS PRINCIPAIS ── */}
      
        <Tabs
          value={mainTab}
          onChange={(_e, newValue) => setMainTab(newValue)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            px: 1,
            borderTop: `1px solid ${theme.palette.divider}`,
            bgcolor: alpha(theme.palette.background.paper, 0.6),
            '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.82rem', minHeight: 42, gap: 0.8 },
          }}
        >
          <Tab
            id="clientesTab"
            aria-controls="clientesTab-panel"
            icon={<PeopleAltIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Clientes"
          />
          <Tab
            id="dashboardTab"
            aria-controls="dashboardTab-panel"
            icon={<DashboardIcon sx={{ fontSize: 18 }} />}
            iconPosition="start"
            label="Dashboard"
          />
        </Tabs>
      </Paper>

      {/* ── ABA: CLIENTES ── */}
      <Box
        role="tabpanel"
        hidden={mainTab !== 0}
        id="clientesTab-panel"
        aria-labelledby="clientesTab"
      >
        {mainTab === 0 && (
          <>
            {/* Mensagem de erro geral */}
            {error && (
              <Alert severity="error" sx={{ mb: 1.5 }}>
                {error}
              </Alert>
            )}

            {/* Listagem de clientes com filtros, paginação e ordenação server-side */}
            <ClientesListarTab 
              escritorioId={escritorioId} 
              notifyTrigger={clientesNotifyTrigger}
              onSelectionChange={setClientesSelectedCount}
            />

            {/* FAB para mobile */}
            {isMobile && canCreateClient && (
              <Fab
                color="primary"
                aria-label="add"
                sx={{
                  position: 'fixed',
                  bottom: 86,
                  right: 16,
                  zIndex: theme.zIndex.appBar - 1,
                }}
                onClick={handleNewClient}
              >
                <Add />
              </Fab>
            )}
          </>
        )}
      </Box>

      {/* ── ABA: DASHBOARD ── */}
      <Box
        role="tabpanel"
        hidden={mainTab !== 1}
        id="dashboardTab-panel"
        aria-labelledby="dashboardTab"
      >
        {mainTab === 1 && (
          <ClientesDashboardTab escritorioId={escritorioId} />
        )}
      </Box>

      {/* Formulário de cliente (global, fora das abas) */}
      <ClientForm
        open={formOpen}
        onClose={handleCloseForm}
        onSuccess={handleClientSuccess}
        editingClient={editingClient}
      />

    </Box>
  );
};

export default ClientsPage; 